import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Shield, Smartphone } from "lucide-react";
import { authClient } from "@/utils/auth/client";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

interface TotpVerifyFormProps {
    onSuccess?: () => void;
    onCancel?: () => void;
    callbackURL?: string;
}

export const TotpVerifyForm: React.FC<TotpVerifyFormProps> = ({ onSuccess, onCancel, callbackURL = "/" }) => {
    const [verificationCode, setVerificationCode] = useState("");
    const [trustDevice, setTrustDevice] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showBackupCode, setShowBackupCode] = useState(false);
    const [backupCode, setBackupCode] = useState("");

    const navigate = useNavigate();

    // Verify TOTP code mutation
    const verifyTotpMutation = useMutation({
        mutationFn: async (code: string) => {
            const result = await authClient.twoFactor.verifyTotp({
                code,
                trustDevice,
            });
            if (result.error) {
                throw new Error(result.error.message || "Invalid verification code");
            }
            return result.data;
        },
        onSuccess: () => {
            if (onSuccess) {
                onSuccess();
            } else {
                navigate(callbackURL);
            }
        },
        onError: (error: any) => {
            setError(error.message || "Invalid verification code");
        },
    });

    // Verify backup code mutation
    const verifyBackupCodeMutation = useMutation({
        mutationFn: async (code: string) => {
            const result = await authClient.twoFactor.verifyBackupCode({
                code,
                trustDevice,
            });
            if (result.error) {
                throw new Error(result.error.message || "Invalid backup code");
            }
            return result.data;
        },
        onSuccess: () => {
            if (onSuccess) {
                onSuccess();
            } else {
                navigate(callbackURL);
            }
        },
        onError: (error: any) => {
            setError(error.message || "Invalid backup code");
        },
    });

    const handleVerifyTotp = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!verificationCode.trim()) {
            setError("Please enter the verification code");
            return;
        }

        setError(null);
        verifyTotpMutation.mutate(verificationCode);
    };

    const handleVerifyBackupCode = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!backupCode.trim()) {
            setError("Please enter the backup code");
            return;
        }

        setError(null);
        verifyBackupCodeMutation.mutate(backupCode);
    };

    const isLoading = verifyTotpMutation.isPending || verifyBackupCodeMutation.isPending;

    return (
        <Card className="mx-auto w-full max-w-md">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Two-Factor Authentication
                </CardTitle>
                <CardDescription>
                    {showBackupCode
                        ? "Enter one of your backup codes"
                        : "Enter the 6-digit code from your authenticator app"}
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {!showBackupCode ? (
                    // TOTP Code Form
                    <form onSubmit={handleVerifyTotp} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="verification-code" className="flex items-center gap-2">
                                <Smartphone className="h-4 w-4" />
                                Authenticator Code
                            </Label>
                            <Input
                                id="verification-code"
                                type="text"
                                placeholder="123456"
                                value={verificationCode}
                                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                maxLength={6}
                                className="text-center text-lg tracking-widest"
                                autoFocus
                            />
                        </div>

                        <div className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                id="trust-device"
                                checked={trustDevice}
                                onChange={(e) => setTrustDevice(e.target.checked)}
                                className="rounded border-gray-300"
                            />
                            <Label htmlFor="trust-device" className="text-sm">
                                Trust this device for 30 days
                            </Label>
                        </div>

                        <div className="flex gap-2">
                            {onCancel && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={onCancel}
                                    disabled={isLoading}
                                    className="flex-1"
                                >
                                    Cancel
                                </Button>
                            )}
                            <Button
                                type="submit"
                                disabled={isLoading || verificationCode.length !== 6}
                                className="flex-1"
                            >
                                {isLoading ? (
                                    <div className="flex items-center gap-2">
                                        <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                                        Verifying...
                                    </div>
                                ) : (
                                    "Verify"
                                )}
                            </Button>
                        </div>

                        <div className="text-center">
                            <Button
                                type="button"
                                variant="link"
                                onClick={() => setShowBackupCode(true)}
                                className="text-sm"
                            >
                                Use backup code instead
                            </Button>
                        </div>
                    </form>
                ) : (
                    // Backup Code Form
                    <form onSubmit={handleVerifyBackupCode} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="backup-code">Backup Code</Label>
                            <Input
                                id="backup-code"
                                type="text"
                                placeholder="Enter backup code"
                                value={backupCode}
                                onChange={(e) => setBackupCode(e.target.value)}
                                className="text-center"
                                autoFocus
                            />
                        </div>

                        <div className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                id="trust-device-backup"
                                checked={trustDevice}
                                onChange={(e) => setTrustDevice(e.target.checked)}
                                className="rounded border-gray-300"
                            />
                            <Label htmlFor="trust-device-backup" className="text-sm">
                                Trust this device for 30 days
                            </Label>
                        </div>

                        <div className="flex gap-2">
                            {onCancel && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={onCancel}
                                    disabled={isLoading}
                                    className="flex-1"
                                >
                                    Cancel
                                </Button>
                            )}
                            <Button type="submit" disabled={isLoading || !backupCode.trim()} className="flex-1">
                                {isLoading ? (
                                    <div className="flex items-center gap-2">
                                        <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                                        Verifying...
                                    </div>
                                ) : (
                                    "Verify"
                                )}
                            </Button>
                        </div>

                        <div className="text-center">
                            <Button
                                type="button"
                                variant="link"
                                onClick={() => setShowBackupCode(false)}
                                className="text-sm"
                            >
                                Use authenticator code instead
                            </Button>
                        </div>
                    </form>
                )}
            </CardContent>
        </Card>
    );
};
